import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Determine CIA Triad category based on attack surface analysis
 * @param {Object} attackSurface - Attack surface assessment data
 * @param {Object} impact - Impact assessment data
 * @returns {string} - 'Confidentiality', 'Integrity', or 'Availability'
 */
const determineCIACategory = (attackSurface = {}, impact = {}) => {
  // Keywords that indicate each CIA category
  const confidentialityKeywords = [
    'data exposure', 'unauthorized access', 'privacy', 'leak', 'disclosure',
    'exfiltration', 'confidential', 'sensitive data', 'pii', 'authentication',
    'credentials', 'password', 'token', 'secret', 'encryption'
  ];

  const integrityKeywords = [
    'manipulation', 'modification', 'tampering', 'corruption', 'alteration',
    'injection', 'integrity', 'validation', 'forgery', 'spoofing',
    'unauthorized change', 'data corruption', 'model poisoning', 'backdoor'
  ];

  const availabilityKeywords = [
    'denial', 'dos', 'ddos', 'disruption', 'unavailable', 'downtime',
    'resource exhaustion', 'availability', 'service disruption', 'outage',
    'crash', 'hang', 'timeout', 'overload', 'flood'
  ];

  // Combine all text from attack surface and impact for analysis
  const textToAnalyze = [
    ...Object.keys(attackSurface).map(k => k.toLowerCase()),
    ...Object.values(attackSurface).map(v =>
      typeof v === 'object' ? JSON.stringify(v).toLowerCase() : String(v).toLowerCase()
    ),
    ...Object.keys(impact).map(k => k.toLowerCase()),
    ...Object.values(impact).map(v =>
      typeof v === 'object' ? JSON.stringify(v).toLowerCase() : String(v).toLowerCase()
    )
  ].join(' ');

  // Count keyword matches for each category
  const confidentialityScore = confidentialityKeywords.filter(kw =>
    textToAnalyze.includes(kw)
  ).length;

  const integrityScore = integrityKeywords.filter(kw =>
    textToAnalyze.includes(kw)
  ).length;

  const availabilityScore = availabilityKeywords.filter(kw =>
    textToAnalyze.includes(kw)
  ).length;

  // Return category with highest score, default to Confidentiality if tied
  if (availabilityScore > confidentialityScore && availabilityScore > integrityScore) {
    return 'Availability';
  } else if (integrityScore > confidentialityScore && integrityScore >= availabilityScore) {
    return 'Integrity';
  } else {
    return 'Confidentiality';
  }
};

const SecurityReportData = () => {
  const securityState = useSelector(state => state.security);

  // DEBUG: Log security state from Redux
  console.log('[SecurityReportData] Full securityState:', securityState);
  console.log('[SecurityReportData] securityState.byAssessment:', securityState?.byAssessment);
  console.log('[SecurityReportData] assessments count:', Object.keys(securityState?.byAssessment ?? {}).length);

  const securityReportData = useMemo(() => {
    const dataByModel = {};
    const dataByCategory = {};

    // Iterate over each assessment (RTK structure: byAssessment instead of byModel)
    Object.entries(securityState.byAssessment ?? {}).forEach(([assessmentUuid, assessmentData]) => {
      // Extract scenarios and controls from the new RTK structure
      const { scenarios = {}, controls = {}, activeModelId } = assessmentData || {};

      // Use activeModelId as the model UUID for backward compatibility
      const modelUuid = activeModelId || assessmentUuid;

      console.log(`[SecurityReportData] Assessment ${assessmentUuid} (Model: ${modelUuid}):`, {
        scenariosCount: Object.keys(scenarios).length,
        scenariosKeys: Object.keys(scenarios).slice(0, 3),
        controlsCount: Object.keys(controls).length
      });

      // Convert scenarios to risks format for compatibility with RiskLandscape
      const risks = Object.entries(scenarios).map(([scenarioId, scenario]) => {
        // Use stored threat_level from database (calculated on save)
        // Falls back to 'Not Assessed' if not yet calculated
        const threatLevel = scenario.threat_level || scenario.threatLevel || 'Not Assessed';

        const attackSurface = scenario.attackSurface || {};

        // Determine CIA category from attack surface and impact data
        const ciaCategory = determineCIACategory(attackSurface, scenario.impact || {});

        return {
          risk_id: scenarioId,
          scenario_id: scenarioId,
          name: scenario.name || 'Unnamed Scenario',
          category: ciaCategory, // CIA Triad categorization
          threat_level: threatLevel,  // Use stored threat_level from database
          status: scenario.status || 'pending',
          updatedAt: scenario.updatedAt,
          mappedAgents: scenario.mappedAgents || [],
          attributes: scenario.attributes || {},
          attackSurface: attackSurface,
          impact: scenario.impact || {},
          assessmentData: scenario.assessmentData || {}
        };
      });

      // Filter controls that belong to scenarios in this assessment
      const assessmentControls = Object.values(controls).filter(control =>
        scenarios[control.scenarioId] !== undefined
      );

      // Organize data by model
      dataByModel[modelUuid] = {
        modelUuid,
        risks: risks.map(risk => ({
          ...risk,
          controls: assessmentControls.filter(c => c.scenarioId === risk.scenario_id) // Attach controls using scenario ID
        })),
        controls: controls
      };

      // Organize risks by category
      risks.forEach(risk => {
        if (!risk || !risk.category) return; // Skip invalid risks

        const category = risk.category.toLowerCase(); // Normalize the category for consistent access
        if (!dataByCategory[category]) {
          dataByCategory[category] = [];
        }
        // Add risk and its associated controls to the category
        dataByCategory[category].push({
          ...risk,
          modelUuid: modelUuid,
          controls: assessmentControls.filter(c => c.scenarioId === risk.scenario_id), // Use scenario ID for controls
        });
      });
    });

    return { dataByModel, dataByCategory };
  }, [securityState]);

  return securityReportData;
};

export default SecurityReportData;